import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import {
  beginAliasEntry,
  beginCustomHostnameEntry,
  beginLegacyEntry,
  beginPlatformEntry,
  isPlatformHost,
  clearAliasResolvedRestaurantId,
  getStorefrontEntryMode,
  isCustomHostnameCandidate,
  matchAliasSlugFromPath,
  readLegacyLocalhostRestaurantId,
  setAliasResolvedRestaurantId,
  setCustomHostnameResolvedRestaurantId,
  type PublicTenantStatus,
  type StorefrontEntryMode,
} from "@/config/restaurant-id";
import { fetchStorefrontBootstrap } from "@/services/storefront-bootstrap";
import {
  invalidateMenuCatalogCache,
} from "@/services/client-query-cache";
import { clearLatestMenuCatalogSnapshot } from "@/domain/cart/menu-catalog.registry";
import {
  planFinalBillingOnCommittedRestaurantId,
  planFinalBillingOnTenantLost,
} from "@/domain/tenant/final-billing-tenant-isolation";
import { clearActiveUnpaidOrder } from "@/slices/checkout-slice";
import { clearAddress as clearCheckoutAddress } from "@/slices/checkout-address-slice";
import {
  clearCheckoutInstructions,
  clearPriceChangeAcknowledgement,
  resetCheckoutFulfilmentForSession,
} from "@/slices/checkout-ux-slice";
import { clearFinalBilling } from "@/slices/final-billing-slice";
import { clearDestinationSession } from "@/domain/destination/destination-session";
import store from "@/store/store";

export type PublicTenantContextValue = {
  status: PublicTenantStatus;
  entryMode: Exclude<StorefrontEntryMode, "unset">;
  slug: string | null;
  restaurantId: string;
  error: Error | null;
  isReady: boolean;
  refetch: () => Promise<void>;
};

const PublicTenantContext = createContext<PublicTenantContextValue | null>(null);

type PublicTenantProviderProps = {
  children: React.ReactNode;
};

function clearTenantSensitiveClientState(): void {
  try {
    invalidateMenuCatalogCache();
  } catch {
    /* ignore */
  }
  try {
    clearLatestMenuCatalogSnapshot();
  } catch {
    /* ignore */
  }
}

/**
 * lastFinalBilling, activeUnpaidOrder, and destination coordinates are
 * tenant-local in-memory leftovers. Invalidate them only when the committed
 * authoritative restaurantId changes — never on loading/empty ids
 * (same-tenant bootstrap would wipe legitimate checkout recovery).
 * Dispatch must complete before the next tenant's checkout children can mount.
 * Payment-await-session ownership is independent and is not cleared here.
 */
function applyCommittedRestaurantId(
  committedRef: React.MutableRefObject<string | null>,
  incomingRestaurantId: string
): void {
  const plan = planFinalBillingOnCommittedRestaurantId(
    committedRef.current,
    incomingRestaurantId
  );
  committedRef.current = plan.nextCommitted;
  if (plan.clearFinalBilling) {
    store.dispatch(clearFinalBilling());
    store.dispatch(clearActiveUnpaidOrder());
    store.dispatch(clearCheckoutInstructions());
    store.dispatch(clearPriceChangeAcknowledgement());
    store.dispatch(resetCheckoutFulfilmentForSession());
    // Checkout address selection is tenant-local (Redux only; the per-restaurant
    // sessionStorage mirror keeps each restaurant's own entry).
    store.dispatch(clearCheckoutAddress());
    clearDestinationSession();
  }
}

function applyTenantLost(committedRef: React.MutableRefObject<string | null>): void {
  const plan = planFinalBillingOnTenantLost(committedRef.current);
  committedRef.current = plan.nextCommitted;
  if (plan.clearFinalBilling) {
    store.dispatch(clearFinalBilling());
    store.dispatch(clearActiveUnpaidOrder());
    store.dispatch(clearCheckoutInstructions());
    store.dispatch(clearPriceChangeAcknowledgement());
    store.dispatch(resetCheckoutFulfilmentForSession());
    store.dispatch(clearCheckoutAddress());
    clearDestinationSession();
  }
}

function resolveEntryModeFromLocation(
  pathname: string,
  hostname: string
): {
  mode: "alias" | "custom_hostname" | "legacy" | "platform";
  aliasSlug: string | null;
} {
  const aliasSlug = matchAliasSlugFromPath(pathname);
  if (aliasSlug) {
    return { mode: "alias", aliasSlug };
  }
  // Ordered before the custom-hostname check: a platform host is a well-formed FQDN
  // and would otherwise be taken for a restaurant custom hostname. `/r/{slug}` still
  // wins above it, so rasoix.app/r/{slug} keeps resolving as an alias storefront.
  if (isPlatformHost(hostname)) {
    return { mode: "platform", aliasSlug: null };
  }
  if (isCustomHostnameCandidate(hostname)) {
    return { mode: "custom_hostname", aliasSlug: null };
  }
  return { mode: "legacy", aliasSlug: null };
}

/**
 * Establishes storefront entry mode from the URL and bootstraps server-derived RestaurantId
 * for PLATFORM_SLUG / CUSTOM_HOSTNAME. Those modes never read REACT_APP_RESTAURANT_ID.
 * Localhost / IP / single-label hosts fail closed unless explicit legacy opt-in is set.
 * Configured platform hosts resolve no tenant at all and expose entryMode "platform".
 */
export function PublicTenantProvider({ children }: PublicTenantProviderProps) {
  const { pathname } = useLocation();
  const hostname =
    typeof window !== "undefined" ? window.location.hostname : "localhost";
  const entry = resolveEntryModeFromLocation(pathname, hostname);

  const [status, setStatus] = useState<PublicTenantStatus>("idle");
  const [error, setError] = useState<Error | null>(null);
  const [restaurantId, setRestaurantId] = useState("");
  const [activeSlug, setActiveSlug] = useState<string | null>(null);
  const [bootstrapNonce, setBootstrapNonce] = useState(0);
  const committedRestaurantIdRef = useRef<string | null>(null);
  const generationRef = useRef(0);

  useEffect(() => {
    const generation = ++generationRef.current;
    let cancelled = false;
    const ac = new AbortController();

    const isAuthoritative = () =>
      generation === generationRef.current && !cancelled;

    async function run() {
      // Platform host: there is no tenant to resolve, so no bootstrap call is made and
      // RestaurantId stays empty. Tenant-scoped leftovers from a storefront visited
      // earlier in this same origin (menu catalog, final billing, active unpaid order,
      // destination) are dropped here, so arriving at discovery cannot carry another
      // restaurant's checkout state into the next one. The per-restaurant cart is keyed
      // by restaurant id in its own persist store and is untouched.
      if (entry.mode === "platform") {
        beginPlatformEntry();
        clearTenantSensitiveClientState();
        if (!isAuthoritative()) return;
        applyTenantLost(committedRestaurantIdRef);
        setActiveSlug(null);
        setRestaurantId("");
        // "resolved" means entry mode is settled, not that a tenant exists. Platform
        // consumers branch on entryMode; isReady stays false because RestaurantId is
        // empty, so any storefront gate reached by mistake still fails closed.
        setStatus("resolved");
        setError(null);
        return;
      }

      if (entry.mode === "alias" && entry.aliasSlug) {
        beginAliasEntry(entry.aliasSlug);
        clearAliasResolvedRestaurantId();
        clearTenantSensitiveClientState();
        if (!isAuthoritative()) return;
        setActiveSlug(entry.aliasSlug);
        setRestaurantId("");
        setStatus("loading");
        setError(null);

        try {
          const boot = await fetchStorefrontBootstrap(
            { mode: "alias", slug: entry.aliasSlug },
            ac.signal
          );
          if (!isAuthoritative()) return;
          applyCommittedRestaurantId(committedRestaurantIdRef, boot.restaurant_id);
          setAliasResolvedRestaurantId(boot.restaurant_id);
          setRestaurantId(boot.restaurant_id);
          setActiveSlug(boot.slug);
          setStatus("resolved");
          setError(null);
        } catch (e) {
          if (!isAuthoritative()) return;
          if (ac.signal.aborted) return;
          applyTenantLost(committedRestaurantIdRef);
          clearAliasResolvedRestaurantId();
          setRestaurantId("");
          const err = e instanceof Error ? e : new Error(String(e));
          const statusCode = (err as Error & { status?: number }).status;
          setStatus(statusCode === 404 || statusCode === 400 ? "unavailable" : "error");
          setError(err);
        }
        return;
      }

      if (entry.mode === "custom_hostname") {
        beginCustomHostnameEntry();
        clearAliasResolvedRestaurantId();
        clearTenantSensitiveClientState();
        if (!isAuthoritative()) return;
        setActiveSlug(null);
        setRestaurantId("");
        setStatus("loading");
        setError(null);

        try {
          const boot = await fetchStorefrontBootstrap(
            { mode: "custom_hostname", slug: null },
            ac.signal
          );
          if (!isAuthoritative()) return;
          applyCommittedRestaurantId(committedRestaurantIdRef, boot.restaurant_id);
          setCustomHostnameResolvedRestaurantId(boot.restaurant_id);
          setRestaurantId(boot.restaurant_id);
          // CUSTOM_HOSTNAME: do not treat hostname as platform slug.
          setActiveSlug(null);
          setStatus("resolved");
          setError(null);
        } catch (e) {
          if (!isAuthoritative()) return;
          if (ac.signal.aborted) return;
          applyTenantLost(committedRestaurantIdRef);
          clearAliasResolvedRestaurantId();
          setRestaurantId("");
          const err = e instanceof Error ? e : new Error(String(e));
          const statusCode = (err as Error & { status?: number }).status;
          setStatus(statusCode === 404 || statusCode === 400 ? "unavailable" : "error");
          setError(err);
        }
        return;
      }

      const STOREFRONT_UNAVAILABLE = "This storefront is not available.";

      beginLegacyEntry();
      if (!isAuthoritative()) return;
      const legacyId = readLegacyLocalhostRestaurantId();
      if (!legacyId) {
        applyTenantLost(committedRestaurantIdRef);
        setActiveSlug(null);
        setRestaurantId("");
        setStatus("unavailable");
        setError(new Error(STOREFRONT_UNAVAILABLE));
        return;
      }

      applyCommittedRestaurantId(committedRestaurantIdRef, legacyId);
      setActiveSlug(null);
      setRestaurantId(legacyId);
      setStatus("resolved");
      setError(null);
    }

    void run();
    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [entry.mode, entry.aliasSlug, hostname, bootstrapNonce]);

  const refetch = useCallback(async () => {
    setBootstrapNonce((n) => n + 1);
  }, []);

  const value = useMemo<PublicTenantContextValue>(() => {
    const mode = getStorefrontEntryMode();
    // Platform is reported from the synchronously-derived entry, not from module state,
    // so the very first render of a platform host already says "platform". Taking it from
    // module state would report "legacy" until the effect runs and briefly mount the
    // storefront gate on a host that has no storefront.
    let exposedMode: Exclude<StorefrontEntryMode, "unset">;
    if (entry.mode === "platform") {
      exposedMode = "platform";
    } else if (mode === "unset") {
      exposedMode = "legacy";
    } else {
      exposedMode = mode;
    }
    // URL slug changed but bootstrap has not committed the new tenant yet.
    // Treat as loading so checkout unmounts before A's lastFinalBilling /
    // activeUnpaidOrder can render or fire retry/cancel on B's route.
    const aliasTransitionPending =
      entry.mode === "alias" &&
      Boolean(entry.aliasSlug) &&
      Boolean(activeSlug) &&
      entry.aliasSlug !== activeSlug;
    const viewStatus: PublicTenantStatus = aliasTransitionPending ? "loading" : status;
    const viewRestaurantId = aliasTransitionPending ? "" : restaurantId;
    return {
      status: viewStatus,
      entryMode: exposedMode,
      slug: activeSlug,
      restaurantId: viewRestaurantId,
      error,
      isReady: viewStatus === "resolved" && Boolean(viewRestaurantId.trim()),
      refetch,
    };
  }, [status, activeSlug, restaurantId, error, refetch, entry.mode, entry.aliasSlug]);

  return (
    <PublicTenantContext.Provider value={value}>
      {children}
    </PublicTenantContext.Provider>
  );
}

export function usePublicTenant(): PublicTenantContextValue {
  const ctx = useContext(PublicTenantContext);
  if (!ctx) {
    throw new Error("usePublicTenant must be used within PublicTenantProvider");
  }
  return ctx;
}
